import { Check, ChevronDown, Palette, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { brandService } from "@/lib/services";
import type { BrandSummary } from "@/types";

export interface BrandPickerProps {
  selectedBrandUuid: string | null;
  onSelect: (uuid: string | null) => void;
  disabled?: boolean;
}

/**
 * Brand picker for the chat header.
 *
 * Loads the brands the user can generate with and lets them choose one for the
 * session. "Default brand" (null) uses the company default. Rendered only when
 * the ``multiBrand`` feature flag is on; hidden entirely when fewer than two
 * brands exist (nothing meaningful to choose).
 */
export function BrandPicker({ selectedBrandUuid, onSelect, disabled = false }: Readonly<BrandPickerProps>) {
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const res = await brandService.list();
        if (active) setBrands(res.data);
      } catch (err) {
        console.error("Failed to load brands:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.uuid === selectedBrandUuid) ?? null,
    [brands, selectedBrandUuid],
  );

  // Nothing to choose between — don't clutter the header.
  if (brands.length < 2) return null;

  const matches = (() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? brands.filter((b) => b.name.toLowerCase().includes(q) || (b.audience ?? "").toLowerCase().includes(q))
      : brands;
    return [...list].sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name));
  })();

  const handleSelect = (uuid: string | null) => {
    onSelect(uuid);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-label="Select brand"
        className="flex items-center gap-2 bg-surface-secondary text-content-primary pl-3 pr-2 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed max-w-[14rem]"
      >
        <Palette className="w-4 h-4 text-content-tertiary shrink-0" />
        <span className="truncate text-sm">{selectedBrand ? selectedBrand.name : "Default brand"}</span>
        <ChevronDown className="w-4 h-4 text-content-tertiary shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-72 max-h-[24rem] overflow-hidden flex flex-col bg-surface-primary border border-border-primary rounded-xl shadow-xl right-0">
          <div className="p-2 border-b border-border-primary">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary pointer-events-none" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search brands…"
                className="w-full bg-surface-secondary text-content-primary text-sm pl-9 pr-3 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>

          <div className="overflow-y-auto py-1">
            <BrandRow
              label="Default brand"
              sublabel="Company default"
              selected={!selectedBrandUuid}
              onClick={() => handleSelect(null)}
            />
            {matches.map((brand) => (
              <BrandRow
                key={brand.uuid}
                label={brand.name}
                sublabel={brand.audience || (brand.isDefault ? "Default" : undefined)}
                selected={brand.uuid === selectedBrandUuid}
                onClick={() => handleSelect(brand.uuid)}
              />
            ))}
            {matches.length === 0 && query.trim() && (
              <div className="px-3 py-4 text-sm text-content-tertiary text-center">No brands match "{query}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BrandRow({
  label,
  sublabel,
  selected,
  onClick,
}: Readonly<{ label: string; sublabel?: string; selected: boolean; onClick: () => void }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-interactive-hover transition ${
        selected ? "bg-brand-primary/10" : ""
      }`}
    >
      <Palette className="w-4 h-4 text-content-tertiary shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-content-primary truncate">{label}</span>
        {sublabel && <span className="block text-xs text-content-tertiary truncate">{sublabel}</span>}
      </span>
      {selected && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
    </button>
  );
}
